<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="名称" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入供应商名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="分级" prop="tier">
        <el-select v-model="queryParams.tier" class="!w-240px" clearable placeholder="请选择分级">
          <el-option
            v-for="item in ProductSupplierTierOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-240px" clearable placeholder="请选择状态">
          <el-option
            v-for="item in ProductSupplierStatusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['product:supplier:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" stripe>
      <el-table-column align="center" label="编号" min-width="80" prop="id" />
      <el-table-column align="center" label="名称" min-width="140" prop="name" />
      <el-table-column align="center" label="联系人" min-width="100" prop="contact" />
      <el-table-column align="center" label="手机号" min-width="120" prop="mobile" />
      <el-table-column align="center" label="分级" min-width="90" prop="tier">
        <template #default="{ row }">
          {{ tierLabel(row.tier) }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="状态" min-width="100" prop="status">
        <template #default="{ row }">
          <el-tag v-if="row.status === ProductSupplierStatus.ENABLE" type="success">启用</el-tag>
          <el-tag v-else-if="row.status === ProductSupplierStatus.DISABLE" type="info">停用</el-tag>
          <el-tag v-else-if="row.status === ProductSupplierStatus.BLACKLIST" type="danger"
            >黑名单</el-tag
          >
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="排序" min-width="80" prop="sort" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" label="操作" width="240">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['product:supplier:update']"
            link
            type="primary"
            @click="openForm('update', row.id)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['product:supplier-account:update']"
            link
            type="primary"
            @click="openAccount(row)"
          >
            登录账号
          </el-button>
          <el-button
            v-hasPermi="['product:supplier:delete']"
            link
            type="danger"
            @click="handleDelete(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <SupplierForm ref="formRef" @success="getList" />

  <Dialog v-model="accountVisible" title="供应商登录账号" width="520px">
    <el-form label-width="110px">
      <el-form-item label="供应商">{{ accountSupplier?.name }}</el-form-item>
      <el-form-item label="当前账号">
        {{ accountInfo?.username || '尚未创建' }}
      </el-form-item>
      <el-form-item label="登录用户名" required>
        <el-input v-model="accountForm.username" placeholder="登录用户名" />
      </el-form-item>
      <el-form-item label="登录密码" required>
        <el-input v-model="accountForm.password" type="password" show-password placeholder="新密码" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="accountVisible = false">取消</el-button>
      <el-button type="primary" :loading="accountLoading" @click="submitAccount">保存</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import {
  deleteSupplier,
  getSupplierPage,
  ProductSupplierStatus,
  ProductSupplierStatusOptions,
  ProductSupplierTierOptions,
  type SupplierVO
} from '@/api/mall/product/supplier'
import {
  createOrResetSupplierAccount,
  getSupplierAccount
} from '@/api/mall/product/pool'
import SupplierForm from './SupplierForm.vue'

defineOptions({ name: 'ProductSupplier' })

const message = useMessage()
const { t } = useI18n()

const loading = ref(true)
const total = ref(0)
const list = ref<SupplierVO[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined as string | undefined,
  status: undefined as number | undefined,
  tier: undefined as number | undefined
})
const queryFormRef = ref()
const formRef = ref()

const accountVisible = ref(false)
const accountLoading = ref(false)
const accountSupplier = ref<SupplierVO>()
const accountInfo = ref<{ username?: string } | null>(null)
const accountForm = reactive({ username: '', password: '' })

const tierLabel = (tier?: number) =>
  ProductSupplierTierOptions.find((i) => i.value === tier)?.label || '-'

const getList = async () => {
  loading.value = true
  try {
    const data = await getSupplierPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

const openAccount = async (row: SupplierVO) => {
  accountSupplier.value = row
  accountForm.username = ''
  accountForm.password = ''
  accountInfo.value = await getSupplierAccount(row.id!)
  if (accountInfo.value?.username) {
    accountForm.username = accountInfo.value.username
  }
  accountVisible.value = true
}

const submitAccount = async () => {
  if (!accountSupplier.value?.id || !accountForm.username || !accountForm.password) {
    message.warning('请填写用户名和密码')
    return
  }
  accountLoading.value = true
  try {
    await createOrResetSupplierAccount({
      supplierId: accountSupplier.value.id,
      username: accountForm.username,
      password: accountForm.password
    })
    message.success('账号已保存')
    accountVisible.value = false
  } finally {
    accountLoading.value = false
  }
}

const handleDelete = async (id: number) => {
  try {
    await message.delConfirm()
    await deleteSupplier(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

onMounted(() => {
  getList()
})
</script>

<style scoped>
/* 避免供应商登录账号弹窗中表单标签换行 */
:deep(.el-form-item__label) {
  white-space: nowrap;
}
</style>
